import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clampPageToRowCount } from 'common/table/utils/clampPageToRowCount';
import { TableV3Ref } from 'common/table';
import { getCandidateProfileTableColumns } from 'modules/candidate-profile/tables/columns/candidateProfileTableColumns';
import { useCandidateProfileDuplicateCheckForTable } from 'modules/sourcing-internal/tables/hooks/useCandidateProfileDuplicateCheckForTable';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import { useIntegration } from 'services/integration/hooks';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { formatAtsProviderName } from 'core/utils/formatAtsProviderName';
import { AddToJobCandidate } from 'modules/ats/components/AddToJob/AddToJobDrawerContent';
import { useAddToJobDrawer } from 'modules/ats/hooks/useAddToJobDrawer';
import { useApolloClient } from '@apollo/client';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useOrganizationCustomFieldConfigs } from 'modules/candidate-profile/hooks/useOrganizationCustomFieldConfigs';
import { useSourcingExplanations } from 'modules/candidate-profile/hooks/useSourcingExplanations';
import {
  CandidateFilterGroups,
  CandidateFilterState,
  ExplanationContext
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { TableV3Filters } from 'common/table/components/TableV3';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import { usePathsV2 } from 'core/services/navigation/usePathsV2';
import { SearchCandidateVectorCandidateProfile } from 'modules/candidate-profile/types';
import { useCandidateListStore } from 'modules/candidate-profile/stores/useCandidateListStore';
import { useAddToTalentListDrawer } from 'modules/candidate-profile/hooks/useAddToTalentListDrawer';
import { useAddToWorkflowDrawer } from 'modules/candidate-profile/hooks/useAddToWorkflowDrawer';
import { useAddToWorkflowModal } from 'modules/candidate-profile/hooks/useAddToWorkflowModal';
import { useInternalSourcingWorkflowName } from 'modules/candidate-profile/hooks/useInternalSourcingWorkflowName';
import { usePreselectedWorkflowId } from 'modules/candidate-profile/hooks/usePreselectedWorkflowId';
import { AddToWorkflowCandidate } from 'modules/candidate-profile/components/AddToWorkflowDrawerContent';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';
import {
  useAddCandidateProfilesToListMutation,
  useGetCandidateBatchQuery,
  useRemoveCandidateProfilesFromListMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { SearchCandidateProfileListsDocument } from 'generated-graphql/graphql';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast';
import { waitForOpenSearchAndWeaviate } from 'modules/candidate-profile/utils/waitForOpenSearchAndWeaviate';
import {
  CANDIDATE_PROFILE_TABLE_ID,
  CANDIDATE_PROFILE_TABLE_SCROLL_IGNORED_PARAMS
} from 'modules/candidate-profile/constants';
import { useAddToJobAction } from 'modules/candidate-profile/hooks/useAddToJobAction';
import {
  getPaginationFromURL,
  savePaginationToURL
} from 'common/table/utils/tableV3Storage';
import {
  CandidateSort,
  sortCandidateProfiles
} from 'modules/candidate-profile/tables/sorting/candidateSort';
import { useResetPageOnFiltersChange } from 'modules/candidate-profile/tables/hooks/useResetPageOnFiltersChange';
import {
  useTableScrollRestoration,
  SCROLL_CONTAINER_SELECTOR
} from 'common/table/hooks/useTableScrollRestoration';

const getRemoveFromListModalTitle = (count: number) =>
  count === 1
    ? 'Remove candidate from list'
    : `Remove ${count} candidates from list`;

const getRemoveFromListModalSubtitle = (count: number) =>
  count === 1
    ? 'Are you sure you want to remove this candidate from the list?'
    : `Are you sure you want to remove these ${count} candidates from the list?`;

type UseCandidateProfileTableLogicProps = {
  candidateProfileListProps: CandidateProfileListProps;
  tableRef: React.RefObject<TableV3Ref<CandidateProfileTableColumns>>;
  filters: TableV3Filters<CandidateProfileTableColumns> | undefined;
  filterConditions: CandidateFilterState[];
  filterGroups: CandidateFilterGroups;
  sessionId: string | null | undefined;
  candidateProfiles: SearchCandidateVectorCandidateProfile[];
  explanationContext: ExplanationContext | null;
  hasSearchResult: boolean;
  isConversationLoading: boolean;
  /** The pending chat message's id; set while a chat turn runs. */
  chatTurnId: string | null;
  selectedCandidateIds: string[];
  onRefetchVectorSearch: () => Promise<{ data: unknown }>;
};

export const useCandidateProfileTableLogic = ({
  candidateProfileListProps,
  tableRef,
  sessionId,
  filters,
  filterConditions,
  filterGroups,
  candidateProfiles,
  explanationContext,
  hasSearchResult,
  isConversationLoading,
  chatTurnId,
  selectedCandidateIds,
  onRefetchVectorSearch
}: UseCandidateProfileTableLogicProps) => {
  const { navigateToPath } = usePathsV2();
  const { openAddToTalentListDrawer } = useAddToTalentListDrawer();
  const { openAddToWorkflowDrawer } = useAddToWorkflowDrawer();
  const { openAddToWorkflowModal } = useAddToWorkflowModal();
  const { showToast } = useToast();
  const { openModal } = useModalV2();
  const [addCandidateProfilesToList] = useAddCandidateProfilesToListMutation();
  const [removeCandidateProfilesFromList] =
    useRemoveCandidateProfilesFromListMutation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Organization details
  const apolloClient = useApolloClient();
  const organization = useOrganization();
  const { customFieldConfigs } = useOrganizationCustomFieldConfigs();
  const vectorDbTenantName = organization?.vectorDbTenantName || 'default';
  const integration = useRecoilValue(integrationAtom);
  const rawIntegrationProvider = integration?.provider || 'ATS';
  const integrationProvider = formatAtsProviderName(rawIntegrationProvider);
  const { isConnected: isAtsConnected } = useIntegration();
  const isWorkflowBuilderEnabled = Boolean(useFlag(Flags.WORKFLOW_BUILDER));
  const setIntegrationProvider = useCandidateListStore(
    (state) => state.setIntegrationProvider
  );

  const { openAddToJobDrawer } = useAddToJobDrawer();

  // For analysis lists, the batch may be tied to an ATS job we preselect in the
  // Add to job drawer.
  const analysisCandidateBatchId =
    candidateProfileListProps.type ===
      CandidateProfileListType.ANALYSIS_CANDIDATES_LIST ||
    candidateProfileListProps.type ===
      CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL
      ? candidateProfileListProps.candidateBatchId
      : undefined;

  const { data: candidateBatchData } = useGetCandidateBatchQuery({
    variables: { id: analysisCandidateBatchId ?? '' },
    skip: !analysisCandidateBatchId
  });
  const analysisAtsJobId =
    candidateBatchData?.getCandidateBatch?.atsJobId ?? undefined;

  useEffect(() => {
    setIntegrationProvider(integrationProvider);
  }, [integrationProvider]);

  // Read pagination from initial URL
  const initialPagination = useMemo(
    () => getPaginationFromURL(searchParams, CANDIDATE_PROFILE_TABLE_ID),
    []
  );

  const [currentPage, setCurrentPage] = useState(initialPagination?.page ?? 0);
  const [pageSize, setPageSize] = useState(initialPagination?.pageSize ?? 25);

  const [sort, setSort] = useState<CandidateSort | null>(null);

  const isInitialMountRef = useRef(true);

  // Sync pagination to URL (skip initial mount)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    savePaginationToURL(
      searchParams,
      setSearchParams,
      { page: currentPage, pageSize },
      CANDIDATE_PROFILE_TABLE_ID
    );
  }, [currentPage, pageSize]);

  // Sorted before mapping: the row mapper formats dates into display strings.
  const sortedCandidateProfiles = useMemo(
    () => sortCandidateProfiles(candidateProfiles, sort, customFieldConfigs),
    [candidateProfiles, sort, customFieldConfigs]
  );

  const { isDuplicateCheckLoading, candidates: mappedCandidateProfiles } =
    useCandidateProfileDuplicateCheckForTable({
      candidateProfileListProps,
      candidateProfiles: sortedCandidateProfiles
    });

  const handleSort = useCallback((nextSort: CandidateSort | null) => {
    setSort(nextSort);
    setCurrentPage(0);
  }, []);

  // Compute paginated data first - this is what actually gets rendered in the table
  const paginatedData = useMemo(() => {
    if (isConversationLoading || mappedCandidateProfiles.length === 0)
      return [];

    const startIndex = currentPage * pageSize;
    const endIndex = startIndex + pageSize;
    return mappedCandidateProfiles.slice(startIndex, endIndex);
  }, [mappedCandidateProfiles, currentPage, pageSize, isConversationLoading]);

  // The search's order is the ranking; a sort only changes what is on screen.
  const rankedIds = useMemo(
    () => candidateProfiles.map((profile) => profile.candidate_profile_id),
    [candidateProfiles]
  );

  const visibleCandidateIds = useMemo(
    () => paginatedData.map((row) => row.id),
    [paginatedData]
  );

  const { explanationsById, pendingIds: pendingExplanationIds } =
    useSourcingExplanations({
      context: explanationContext,
      visibleCandidateIds,
      rankOffset: currentPage * pageSize,
      rankedIds
    });

  const explanations = useMemo(
    () => ({ explanationsById, pendingIds: pendingExplanationIds }),
    [explanationsById, pendingExplanationIds]
  );

  // AG Grid doesn't repaint cells on a context change.
  useEffect(() => {
    tableRef.current?.refreshColumns(['explanation']);
  }, [explanationsById, pendingExplanationIds]);

  // Scroll restoration: Wait for ALL data loading to complete before restoring scroll.
  //
  // Why each loading state matters:
  // - isConversationLoading: True while AI conversation/search is processing. Also true
  //   during initial data fetch and filter changes (via parent's `loading` prop).
  //   If we scroll while this is true, table shows empty/loading state.
  // - isDuplicateCheckLoading: True while checking candidates against ATS for duplicates.
  //   This runs AFTER main data loads, so we must wait for it too.
  // - mappedCandidateProfiles.length === 0: No data yet (initial load or empty results).
  //   Scrolling with no data would scroll to 0 (nothing to scroll to).
  //
  // All three must be false for the table to have actual rendered rows to scroll to.
  // See useTableScrollRestoration for details on how restoration works.
  const isTableDataLoading =
    isConversationLoading ||
    isDuplicateCheckLoading ||
    mappedCandidateProfiles.length === 0;
  useTableScrollRestoration(
    CANDIDATE_PROFILE_TABLE_ID,
    isTableDataLoading,
    CANDIDATE_PROFILE_TABLE_SCROLL_IGNORED_PARAMS
  );

  const scrollToTop = () => {
    const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR);
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    } else {
      window.scrollTo({ top: 200, behavior: 'auto' });
    }
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    scrollToTop();
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // Reset to first page when page size changes
    scrollToTop();
  };

  // Back to page 0 when a chat turn starts. Not on any search: the first one
  // after mount would discard a page restored from the URL.
  const previousTurnIdRef = useRef(chatTurnId);

  useEffect(() => {
    const previousTurnId = previousTurnIdRef.current;
    previousTurnIdRef.current = chatTurnId;

    if (chatTurnId && chatTurnId !== previousTurnId) {
      setCurrentPage(0);
    }
  }, [chatTurnId]);

  // Rows can go without a search or a filter change, e.g. removed from a talent
  // list, and leave the current page empty. Move back to the last page that
  // still has rows. Not before the rows load: with none yet, every page is empty.
  useEffect(() => {
    if (isTableDataLoading) return;

    const lastPageWithRows = clampPageToRowCount({
      currentPage,
      pageSize,
      totalRows: mappedCandidateProfiles.length
    });

    if (lastPageWithRows !== currentPage) {
      setCurrentPage(lastPageWithRows);
    }
  }, [
    mappedCandidateProfiles.length,
    pageSize,
    currentPage,
    isTableDataLoading
  ]);

  // The search runs on the chips, which hold ranges and chip groups the column
  // filters cannot. A search with no matches still counts as loaded.
  useResetPageOnFiltersChange(
    { filters, conditions: filterConditions, groups: filterGroups },
    !hasSearchResult || isConversationLoading || isDuplicateCheckLoading,
    () => setCurrentPage(0)
  );

  const handleDeselectAll = () => {
    tableRef.current?.deselectAll();
  };

  const removeFromTalentList = useCallback(
    async (candidateProfileIds: string[]) => {
      if (
        candidateProfileListProps.type !==
        CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL
      ) {
        return;
      }

      const count = candidateProfileIds.length;

      openModal({
        title: getRemoveFromListModalTitle(count),
        subtitle: getRemoveFromListModalSubtitle(count),
        primaryButtonText: 'Remove',
        secondaryButtonText: 'Cancel',
        primaryButtonProps: {
          variant: 'tertiary',
          state: 'destructive'
        },
        secondaryButtonProps: { variant: 'primary' },
        onPrimaryAction: async () => {
          await removeCandidateProfilesFromList({
            variables: {
              input: {
                candidateProfileIds,
                candidateProfileListId: candidateProfileListProps.talentPoolId
              }
            }
          });

          await waitForOpenSearchAndWeaviate();

          tableRef.current?.deselectAll();

          await Promise.all([
            apolloClient.refetchQueries({
              include: [SearchCandidateProfileListsDocument]
            }),
            onRefetchVectorSearch()
          ]);

          showToast({
            title:
              count === 1
                ? 'Candidate removed from list'
                : `${count} candidates removed from list`,
            type: ToastType.Success
          });
        }
      });
    },
    [
      candidateProfileListProps,
      onRefetchVectorSearch,
      openModal,
      removeCandidateProfilesFromList,
      apolloClient,
      showToast
    ]
  );

  const handleRemoveFromList = useCallback(
    (candidateProfileId: string) => {
      void removeFromTalentList([candidateProfileId]);
    },
    [removeFromTalentList]
  );

  const handleRemoveSelectedFromTalentList = useCallback(() => {
    if (selectedCandidateIds.length === 0) return;
    void removeFromTalentList(selectedCandidateIds);
  }, [selectedCandidateIds, removeFromTalentList]);

  // From the context, not the text: rows arrive before their explanations.
  const hasAnyExplanation = useMemo(
    () =>
      Boolean(explanationContext) ||
      candidateProfiles.some((profile) => Boolean(profile.explanation)),
    [candidateProfiles, explanationContext]
  );

  const hasAnyScore = useMemo(
    () =>
      candidateProfiles.some(
        (profile) => profile.score !== null && profile.score !== undefined
      ),
    [candidateProfiles]
  );

  // Imperative: a colDef `hide` changed after init doesn't reach the grid.
  useEffect(() => {
    if (!tableRef.current) return;

    tableRef.current?.setColumnsVisible(['explanation'], hasAnyExplanation);
  }, [hasAnyExplanation]);

  useEffect(() => {
    if (!tableRef.current) return;

    tableRef.current?.setColumnsVisible(['score'], hasAnyScore);
  }, [hasAnyScore]);

  const hasAnyExternalUrl = useMemo(
    () => candidateProfiles.some((profile) => Boolean(profile.external_url)),
    [candidateProfiles]
  );

  useEffect(() => {
    if (!tableRef.current) return;

    tableRef.current?.setColumnsVisible(
      ['atsCandidateId'],
      isAtsConnected || hasAnyExternalUrl
    );
  }, [isAtsConnected, hasAnyExternalUrl]);

  // Table columns
  const columns = useMemo(
    () =>
      getCandidateProfileTableColumns({
        candidateProfileListProps,
        apolloClient,
        vectorDbTenantName,
        integrationProvider,
        isAtsConnected,
        hasAnyExternalUrl,
        organizationId: organization?.id || '',
        onRemoveFromList: handleRemoveFromList,
        hasAnyExplanation,
        hasAnyScore,
        customFieldConfigs
      }),
    [
      candidateProfileListProps,
      apolloClient,
      vectorDbTenantName,
      integrationProvider,
      isAtsConnected,
      hasAnyExternalUrl,
      organization?.id,
      handleRemoveFromList,
      hasAnyExplanation,
      hasAnyScore,
      customFieldConfigs
    ]
  );

  const handleInviteToApply = () => {
    if (!sessionId) {
      return;
    }

    switch (candidateProfileListProps.type) {
      case CandidateProfileListType.CANDIDATES_LIST:
      case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
        navigateToPath.ANALYSE_SOURCE_INTERNAL_CAMPAIGN_SETUP_WITH_CANDIDATE_PROFILES(
          {
            sessionId
          }
        );
        break;
      case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
        // The button should not show.
        break;
      case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
      case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
        navigateToPath.ANALYSE_SOURCE_INTERNAL_CAMPAIGN_SETUP_WITH_CANDIDATE_BATCH(
          {
            candidateBatchId: candidateProfileListProps.candidateBatchId,
            sessionId
          }
        );
        break;
    }
  };

  const handleAddToTalentList = useCallback(async () => {
    if (selectedCandidateIds.length === 0) {
      return;
    }

    switch (candidateProfileListProps.type) {
      case CandidateProfileListType.CANDIDATES_LIST:
        openAddToTalentListDrawer({
          selectedCandidateIds,
          onAddToList: async () => {
            handleDeselectAll();
            // Wait for the deselect all to complete before refetching the vector search
            setTimeout(async () => {
              await onRefetchVectorSearch();
            }, 100);
          }
        });
        break;
      case CandidateProfileListType.ADD_CANDIDATES_TO_TALENT_POOL:
        await addCandidateProfilesToList({
          variables: {
            input: {
              candidateProfileIds: selectedCandidateIds,
              candidateProfileListId: candidateProfileListProps.talentPoolId
            }
          }
        });
        await waitForOpenSearchAndWeaviate();

        tableRef.current?.deselectAll();

        await Promise.all([
          apolloClient.refetchQueries({
            include: [SearchCandidateProfileListsDocument]
          }),
          onRefetchVectorSearch()
        ]);

        showToast({
          title:
            selectedCandidateIds.length === 1
              ? 'Candidate added to talent list'
              : `${selectedCandidateIds.length} candidates added to talent list`,
          type: ToastType.Success
        });
        break;
      case CandidateProfileListType.CANDIDATES_FROM_TALENT_POOL:
      case CandidateProfileListType.ANALYSIS_CANDIDATES_LIST:
      case CandidateProfileListType.ANALYSIS_CANDIDATES_FROM_TALENT_POOL:
        // This button should not show.
        break;
    }
  }, [selectedCandidateIds, candidateProfileListProps]);

  const addToJobCandidates: AddToJobCandidate[] = useMemo(() => {
    const selectedIdsSet = new Set(selectedCandidateIds);
    return mappedCandidateProfiles
      .filter((profile) => selectedIdsSet.has(profile.id))
      .map((profile) => ({
        candidateProfileId: profile.id,
        name: profile.name,
        atsCandidateId: profile.atsCandidateId || undefined
      }));
  }, [selectedCandidateIds, mappedCandidateProfiles]);

  const handleAddToJob = useCallback(() => {
    if (selectedCandidateIds.length === 0) {
      return;
    }
    openAddToJobDrawer({
      organizationId: organization?.id || '',
      candidates: addToJobCandidates,
      initialAtsJobId: analysisAtsJobId
    });
  }, [
    selectedCandidateIds,
    openAddToJobDrawer,
    organization?.id,
    addToJobCandidates,
    analysisAtsJobId
  ]);

  const { handleAddToJobAction, hasAddToJobAction, isAddToJobActionOverLimit } =
    useAddToJobAction({ selectedCandidateIds });

  // Set when the recruiter arrived from a workflow in the builder, so the picker opens on it.
  const preselectedWorkflowId = usePreselectedWorkflowId();
  const handedOverWorkflowName = useInternalSourcingWorkflowName(
    preselectedWorkflowId
  );

  const addToWorkflowCandidates: AddToWorkflowCandidate[] = useMemo(() => {
    const selectedIdsSet = new Set(selectedCandidateIds);
    return mappedCandidateProfiles
      .filter((profile) => selectedIdsSet.has(profile.id))
      .map((profile) => ({
        candidateProfileId: profile.id,
        name: profile.name
      }));
  }, [selectedCandidateIds, mappedCandidateProfiles]);

  const handleAddToWorkflow = useCallback(() => {
    if (
      selectedCandidateIds.length === 0 ||
      candidateProfileListProps.type !==
        CandidateProfileListType.CANDIDATES_LIST ||
      // Not just hidden: a hand-off URL could still name a workflow for an org that has no
      // builder, and that push must not happen.
      !isWorkflowBuilderEnabled
    ) {
      return;
    }

    // Arrived from the builder to populate one named workflow: confirm and go back, rather
    // than opening a picker for a choice that has already been made.
    if (preselectedWorkflowId) {
      openAddToWorkflowModal({
        workflowId: preselectedWorkflowId,
        workflowName: handedOverWorkflowName,
        candidateProfileIds: selectedCandidateIds,
        onAdded: handleDeselectAll
      });
      return;
    }

    openAddToWorkflowDrawer({
      candidates: addToWorkflowCandidates,
      onAddedToWorkflow: async () => {
        handleDeselectAll();
      }
    });
  }, [
    selectedCandidateIds,
    candidateProfileListProps,
    addToWorkflowCandidates,
    preselectedWorkflowId,
    handedOverWorkflowName,
    isWorkflowBuilderEnabled
  ]);

  return {
    paginatedData,
    explanations,
    columns,
    currentPage,
    pageSize,
    sort,
    handleSort,
    mappedCandidateProfiles,
    isDuplicateCheckLoading,
    integrationProvider,
    handleChangePage,
    handleChangePageSize,
    handleDeselectAll,
    handleInviteToApply,
    handleAddToTalentList,
    handleAddToWorkflow,
    handleRemoveSelectedFromTalentList,
    handleAddToJob,
    handleAddToJobAction,
    hasAddToJobAction,
    isAddToJobActionOverLimit,
    isAtsConnected,
    isFromWorkflowBuilder: Boolean(preselectedWorkflowId),
    isWorkflowBuilderEnabled
  };
};
